/**
 * VisibleChangesContext — dedicated React Context for the
 * `visibleChangeIds` slice of `useChangeNavigation` (T5 / iter-2-hardening).
 *
 * WHY A SEPARATE CONTEXT:
 *
 *   `useChangeNavigation` holds `visibleChangeIds` as a Set state that
 *   updates on every IntersectionObserver tick (i.e. every scroll frame).
 *   The full navigation object is consumed by `ComparisonWorkspaceInner`,
 *   so a scroll-induced `visibleChangeIds` change re-renders the entire
 *   workspace shell, including `ComparisonBody` and the diff trees — even
 *   though their props don't depend on visibility.
 *
 *   To eliminate that waste (the "C-scroll triggers whole-tree reconcile"
 *   problem), we:
 *
 *     1. Keep `visibleChangeIds` in `useChangeNavigation` state (it must
 *        be live state so the hook's IntersectionObserver callback can
 *        batch-update it).
 *     2. Wrap `<Minimap />` in `VisibleChangesContext.Provider` so Minimap
 *        (the only consumer of visibility) reads it through Context.
 *     3. Rely on `ComparisonBody`'s `React.memo` (T3) — its props don't
 *        reference `visibleChangeIds`, so the memo comparator returns
 *        true on a scroll-induced workspace re-render and reconciliation
 *        of the diff tree is skipped.
 *
 *   Net effect: a scroll tick re-renders Minimap's ~48 blocks, not the
 *   diff trees. ~90% scroll waste eliminated.
 *
 * CONSUMER CONTRACT:
 *
 *   - `Minimap` reads `useContext(VisibleChangesContext)` inside its
 *     `MinimapBlock` to derive `isVisible` per row.
 *   - Default value: `new Set()` — keeps Minimap functional when rendered
 *     outside a Provider (defensive; the workspace always wraps it).
 *
 * STABILITY:
 *
 *   The Provider's `value` is the Set reference from
 *   `navigation.visibleChangeIds`. `useChangeNavigation` already returns a
 *   fresh Set only when the contents actually change (see the
 *   IntersectionObserver callback's `changed ? next : prev` guard), so
 *   Context consumers don't churn on no-op ticks.
 */
import { createContext } from 'react';

export const VisibleChangesContext = createContext<ReadonlySet<string>>(
  new Set<string>(),
);
